import { requireService, wrap } from '@repo/di-ez';
import { ExpandDownLight, ExpandUpLight } from '@repo/go-icons';
import { Button, Scrollbar } from '@repo/go-ui';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Trajectory2dChart } from '../trajectory-2d-charts';

import styles from './trajectory-2d-charts-layout.module.scss';

const WHEEL_TICKS_TO_PAGE = 1;
const WHEEL_IDLE_RESET_MS = 400;
const SCROLL_ANIMATION_MS = 380;
const NAV_BUTTON_SIZE = 36;
const NAV_BUTTON_EDGE_OFFSET = 12;
const NAV_BUTTON_HOVER_PADDING = 36;

type ChartsScrollValues = {
  scrollTop: number;
  clientHeight: number;
};

type NavHoverZone = 'top' | 'bottom' | null;

function isChartsScrollValues(value: unknown): value is ChartsScrollValues {
  return (
    typeof value === 'object' &&
    value !== null &&
    'scrollTop' in value &&
    'clientHeight' in value &&
    typeof (value as ChartsScrollValues).scrollTop === 'number' &&
    typeof (value as ChartsScrollValues).clientHeight === 'number'
  );
}

function easeInOutCubic(currentTime: number, start: number, diff: number, duration: number): number {
  const progress = currentTime / (duration / 2);

  if (progress < 1) {
    return (diff / 2) * progress * progress * progress + start;
  }

  return (diff / 2) * ((progress - 2) * (progress - 2) * (progress - 2) + 2) + start;
}

function animateScroll(
  start: number,
  end: number,
  onChange: (value: number) => void,
  onFinish: (value: number) => void,
  duration = SCROLL_ANIMATION_MS,
): () => void {
  let startTime: number | undefined;
  let animationId = 0;

  const step = (currentTime: number) => {
    if (startTime === undefined) {
      startTime = currentTime;
    }

    const elapsed = currentTime - startTime;

    if (elapsed < duration) {
      animationId = requestAnimationFrame(step);
      onChange(easeInOutCubic(elapsed, start, end - start, duration));
      return;
    }

    onChange(end);
    onFinish(end);
  };

  animationId = requestAnimationFrame(step);

  return () => cancelAnimationFrame(animationId);
}

function getIntersectingPages(scrollTop: number, viewportHeight: number, pageCount: number): number[] {
  if (viewportHeight <= 0 || pageCount <= 0) {
    return [0];
  }

  const first = Math.max(0, Math.floor(scrollTop / viewportHeight));
  const last = Math.min(pageCount - 1, Math.floor((scrollTop + viewportHeight - 1) / viewportHeight));
  const pages: number[] = [];

  for (let index = first; index <= last; index += 1) {
    pages.push(index);
  }

  return pages.length > 0 ? pages : [0];
}

function isPointerNearNavButton(
  localX: number,
  localY: number,
  rootWidth: number,
  rootHeight: number,
  position: 'top' | 'bottom',
): boolean {
  const buttonLeft = rootWidth / 2 - NAV_BUTTON_SIZE / 2;
  const buttonTop = position === 'top' ? NAV_BUTTON_EDGE_OFFSET : rootHeight - NAV_BUTTON_EDGE_OFFSET - NAV_BUTTON_SIZE;

  const left = buttonLeft - NAV_BUTTON_HOVER_PADDING;
  const right = buttonLeft + NAV_BUTTON_SIZE + NAV_BUTTON_HOVER_PADDING;
  const top = buttonTop - NAV_BUTTON_HOVER_PADDING;
  const bottom = buttonTop + NAV_BUTTON_SIZE + NAV_BUTTON_HOVER_PADDING;

  return localX >= left && localX <= right && localY >= top && localY <= bottom;
}

function resolveNavHoverZone(localX: number, localY: number, rootWidth: number, rootHeight: number): NavHoverZone {
  const nearTop = isPointerNearNavButton(localX, localY, rootWidth, rootHeight, 'top');
  const nearBottom = isPointerNearNavButton(localX, localY, rootWidth, rootHeight, 'bottom');

  if (nearTop && !nearBottom) {
    return 'top';
  }

  if (nearBottom && !nearTop) {
    return 'bottom';
  }

  if (nearTop && nearBottom) {
    const topCenterY = NAV_BUTTON_EDGE_OFFSET + NAV_BUTTON_SIZE / 2;
    const bottomCenterY = rootHeight - NAV_BUTTON_EDGE_OFFSET - NAV_BUTTON_SIZE / 2;

    return Math.abs(localY - topCenterY) <= Math.abs(localY - bottomCenterY) ? 'top' : 'bottom';
  }

  return null;
}

export const Trajectory2dChartsLayout = wrap(function Trajectory2dChartsLayout() {
  const { t } = useTranslation();
  const uiStore = requireService('trajectoryUiStore');
  const graphStore = requireService('trajectoryGraphStore');

  const configs = graphStore.enabledChart2dConfigs;
  const isExpanded = uiStore.expandState === 'graphExpanded';
  const chartsPerPage = isExpanded ? 2 : 1;
  const pageCount = Math.max(1, Math.ceil(configs.length / chartsPerPage));
  const configIdsKey = configs.map((config) => config.id).join('|');

  const rootRef = useRef<HTMLDivElement>(null);
  const scrollTopRef = useRef(0);
  const activePageRef = useRef(0);
  const viewportHeightRef = useRef(0);
  const isAnimatingRef = useRef(false);
  const disposeAnimationRef = useRef<(() => void) | null>(null);
  const wheelTicksRef = useRef(0);
  const wheelDirectionRef = useRef(0);
  const wheelIdleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [viewportHeight, setViewportHeight] = useState(0);
  const [activePage, setActivePage] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [mountedPages, setMountedPages] = useState<number[]>([0]);
  const [hoverZone, setHoverZone] = useState<NavHoverZone>(null);

  const clampPage = useCallback((page: number) => Math.min(pageCount - 1, Math.max(0, page)), [pageCount]);

  const syncViewportHeight = useCallback((height: number) => {
    if (height <= 0) {
      return;
    }

    viewportHeightRef.current = height;
    setViewportHeight((prev) => (prev === height ? prev : height));
  }, []);

  const settleOnPage = useCallback(
    (page: number) => {
      const nextPage = clampPage(page);
      const height = viewportHeightRef.current;
      const target = nextPage * height;

      activePageRef.current = nextPage;
      scrollTopRef.current = target;
      isAnimatingRef.current = false;
      setActivePage(nextPage);
      setScrollTop(target);
      setMountedPages([nextPage]);
    },
    [clampPage],
  );

  const goToPage = useCallback(
    (page: number) => {
      const nextPage = clampPage(page);
      const height = viewportHeightRef.current;

      if (height <= 0) {
        settleOnPage(nextPage);
        return;
      }

      const target = nextPage * height;
      const start = scrollTopRef.current;

      if (Math.abs(target - start) < 1) {
        settleOnPage(nextPage);
        return;
      }

      disposeAnimationRef.current?.();
      wheelTicksRef.current = 0;
      isAnimatingRef.current = true;
      activePageRef.current = nextPage;
      setActivePage(nextPage);
      setMountedPages(
        Array.from(new Set([...getIntersectingPages(start, height, pageCount), nextPage])).sort(
          (left, right) => left - right,
        ),
      );

      disposeAnimationRef.current = animateScroll(
        start,
        target,
        (value) => {
          scrollTopRef.current = value;
          setScrollTop(value);
        },
        () => {
          disposeAnimationRef.current = null;
          settleOnPage(nextPage);
        },
      );
    },
    [clampPage, pageCount, settleOnPage],
  );

  useEffect(() => {
    disposeAnimationRef.current?.();
    disposeAnimationRef.current = null;
    isAnimatingRef.current = false;
    wheelTicksRef.current = 0;
    wheelDirectionRef.current = 0;
    activePageRef.current = 0;
    scrollTopRef.current = 0;
    setActivePage(0);
    setScrollTop(0);
    setMountedPages([0]);
    setHoverZone(null);
  }, [isExpanded, configIdsKey]);

  useEffect(() => {
    return () => {
      disposeAnimationRef.current?.();

      if (wheelIdleTimeoutRef.current) {
        clearTimeout(wheelIdleTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const root = rootRef.current;

    if (!root || pageCount <= 1) {
      return;
    }

    const resetWheelTicksSoon = () => {
      if (wheelIdleTimeoutRef.current) {
        clearTimeout(wheelIdleTimeoutRef.current);
      }

      wheelIdleTimeoutRef.current = setTimeout(() => {
        wheelTicksRef.current = 0;
        wheelDirectionRef.current = 0;
      }, WHEEL_IDLE_RESET_MS);
    };

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (isAnimatingRef.current || event.deltaY === 0) {
        return;
      }

      const direction = Math.sign(event.deltaY);

      if (direction !== wheelDirectionRef.current) {
        wheelDirectionRef.current = direction;
        wheelTicksRef.current = 0;
      }

      wheelTicksRef.current += 1;
      resetWheelTicksSoon();

      if (wheelTicksRef.current < WHEEL_TICKS_TO_PAGE) {
        return;
      }

      wheelTicksRef.current = 0;
      goToPage(activePageRef.current + direction);
    };

    root.addEventListener('wheel', onWheel, { passive: false, capture: true });

    return () => {
      root.removeEventListener('wheel', onWheel, { capture: true });
    };
  }, [goToPage, pageCount]);

  const handleUpdate = useCallback(
    (event: unknown) => {
      if (!isChartsScrollValues(event)) {
        return;
      }

      const { clientHeight } = event;

      if (clientHeight <= 0) {
        return;
      }

      const prevHeight = viewportHeightRef.current;

      if (prevHeight > 0 && prevHeight !== clientHeight && !isAnimatingRef.current) {
        const target = clampPage(activePageRef.current) * clientHeight;
        scrollTopRef.current = target;
        setScrollTop(target);
      }

      syncViewportHeight(clientHeight);
    },
    [clampPage, syncViewportHeight],
  );

  const handleScroll = useCallback(
    (event: unknown) => {
      if (!isChartsScrollValues(event) || isAnimatingRef.current) {
        return;
      }

      syncViewportHeight(event.clientHeight);
      scrollTopRef.current = event.scrollTop;
      setScrollTop(event.scrollTop);
      setMountedPages(getIntersectingPages(event.scrollTop, event.clientHeight, pageCount));
    },
    [pageCount, syncViewportHeight],
  );

  const handleScrollStop = useCallback(
    (event: unknown) => {
      if (!isChartsScrollValues(event) || isAnimatingRef.current || event.clientHeight <= 0) {
        return;
      }

      const page = clampPage(Math.round(event.scrollTop / event.clientHeight));
      goToPage(page);
    },
    [clampPage, goToPage],
  );

  const handleMouseMove = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    const root = rootRef.current;

    if (!root) {
      return;
    }

    const rect = root.getBoundingClientRect();

    if (rect.width <= 0 || rect.height <= 0) {
      return;
    }

    setHoverZone(resolveNavHoverZone(event.clientX - rect.left, event.clientY - rect.top, rect.width, rect.height));
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoverZone(null);
  }, []);

  const mountedPageSet = useMemo(() => new Set(mountedPages), [mountedPages]);
  const canGoUp = activePage > 0;
  const canGoDown = activePage < pageCount - 1;
  const showUpButton = hoverZone === 'top' && canGoUp;
  const showDownButton = hoverZone === 'bottom' && canGoDown;

  if (!configs.length) {
    return <div className={clsx('dash-body-regular', styles.emptyState)}>{t('Graph.noChartsSelected')}</div>;
  }

  const pageHeight = viewportHeight > 0 ? viewportHeight : undefined;
  const contentHeight = pageHeight ? pageCount * pageHeight : '100%';

  return (
    <div ref={rootRef} className={styles.root} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
      <Scrollbar
        className={styles.layout}
        noScrollX
        scrollTop={scrollTop}
        onScroll={handleScroll}
        onUpdate={handleUpdate}
        onScrollStop={handleScrollStop}
      >
        <div className={styles.content} style={{ height: contentHeight }}>
          {Array.from({ length: pageCount }, (_, pageIndex) => {
            const pageConfigs = configs.slice(pageIndex * chartsPerPage, pageIndex * chartsPerPage + chartsPerPage);
            const isMounted = mountedPageSet.has(pageIndex);

            return (
              <div
                key={pageIndex}
                className={clsx(styles.page, isExpanded ? styles.gridPage : styles.stackPage)}
                style={{ height: pageHeight ?? '100%' }}
              >
                {isMounted
                  ? pageConfigs.map((config) => (
                      <div key={config.id} className={styles.chartSlot}>
                        <Trajectory2dChart config={config} />
                      </div>
                    ))
                  : null}
              </div>
            );
          })}
        </div>
      </Scrollbar>

      <Button
        variant="secondary"
        size="m"
        icon={<ExpandUpLight />}
        className={clsx(styles.navButton, styles.navButtonUp, showUpButton && styles.navButtonVisible)}
        title={t('Graph.scrollChartsUp')}
        onClick={() => goToPage(activePage - 1)}
      />

      <Button
        variant="secondary"
        size="m"
        icon={<ExpandDownLight />}
        className={clsx(styles.navButton, styles.navButtonDown, showDownButton && styles.navButtonVisible)}
        title={t('Graph.scrollChartsDown')}
        onClick={() => goToPage(activePage + 1)}
      />
    </div>
  );
});
